import assert from 'shared/assert'
import map_has from 'shared/map_has'

type Accessory = {
	holster_accessory_ref: number
	product_ref: number
	sort_order: number
	is_attached_to_holster: boolean
	can_be_last_attachment: boolean
	hardware_parent_product_refs: number[]
	hardware_child_product_refs: number[]
}

export default <ACCESSORY extends Accessory>({
	valid_accessories_for_selection,
	hardwared_holster_accessories,
	root_product_ref,
	selected_accessory_refs,
}: {
	valid_accessories_for_selection: ACCESSORY[]
	hardwared_holster_accessories: ACCESSORY[]
	root_product_ref: number
	selected_accessory_refs: Set<number>
}) => valid_accessories_for_selection.reduce((groups, accessory) => {
	const product_ref_to_valid_accessory = new Map(valid_accessories_for_selection.map(accessory => [
		accessory.product_ref,
		accessory,
	]))

	const product_ref_is_selected = (product_ref: number) => {
		const holster_accessory_ref = product_ref_to_valid_accessory.get(product_ref)?.holster_accessory_ref
		return holster_accessory_ref && selected_accessory_refs.has(holster_accessory_ref)
	}

	const sort_product_refs = (a: number, b: number) => {
		assert(map_has(product_ref_to_valid_accessory, a))
		assert(map_has(product_ref_to_valid_accessory, b))
		return product_ref_to_valid_accessory.get(a).sort_order - product_ref_to_valid_accessory.get(b).sort_order
	}

	const product_refs_to_sorted_accessories = (product_refs: number[]) => product_refs
		.map(product_ref => {
			assert(map_has(product_ref_to_valid_accessory, product_ref))
			return product_ref_to_valid_accessory.get(product_ref)
		})
		.sort((a: ACCESSORY, b: ACCESSORY) => a.sort_order - b.sort_order)

	const is_unselected_hardware = accessory.is_attached_to_holster
			&& accessory.hardware_parent_product_refs.length
			&& !selected_accessory_refs.has(accessory.holster_accessory_ref)

	if (!is_unselected_hardware) {
		groups.push([ accessory ])
	} else if (accessory.hardware_parent_product_refs.includes(hardwared_holster_accessories.at(-1)?.product_ref || root_product_ref)) {
		if (accessory.can_be_last_attachment) {
			groups.push([ accessory ])
		} else {
			const unselected_paths_to_potential_last_attachment = get_paths({
				refs: [ accessory.product_ref ],
				target_condition: (product_ref: number) => map_has(product_ref_to_valid_accessory, product_ref)
					&& product_ref_to_valid_accessory.get(product_ref).can_be_last_attachment,
				get_next_refs: product_ref => map_has(product_ref_to_valid_accessory, product_ref)
					? product_ref_to_valid_accessory.get(product_ref).hardware_child_product_refs
						.filter(product_ref => !product_ref_is_selected(product_ref) && map_has(product_ref_to_valid_accessory, product_ref))
						.sort(sort_product_refs)
					: [],
			})

			unselected_paths_to_potential_last_attachment.forEach(path => groups.push(product_refs_to_sorted_accessories(path)))
		}
	} else if (accessory.hardware_child_product_refs.some(product_ref => product_ref_is_selected(product_ref))) {
		const hardwared_child_product_ref = accessory.hardware_child_product_refs.find(product_ref => product_ref_is_selected(product_ref))

		const potential_child_has_parent_attachment = hardwared_holster_accessories
			.findIndex(({ product_ref }) => product_ref === hardwared_child_product_ref) > 0

		if (!potential_child_has_parent_attachment) {
			const unselected_paths_to_root_product_ref = get_paths({
				refs: [ accessory.product_ref ],
				target_condition: product_ref => map_has(product_ref_to_valid_accessory, product_ref)
					&& product_ref_to_valid_accessory.get(product_ref).hardware_parent_product_refs
						.some(parent_product_ref => parent_product_ref === root_product_ref),
				get_next_refs: product_ref => map_has(product_ref_to_valid_accessory, product_ref)
					? product_ref_to_valid_accessory.get(product_ref).hardware_parent_product_refs
						.filter(product_ref => !product_ref_is_selected(product_ref))
						.sort(sort_product_refs)
					: [],
			})

			unselected_paths_to_root_product_ref.forEach(path => groups.push(product_refs_to_sorted_accessories(path)))
		}
	}

	return groups
}, [] as ACCESSORY[][])

// Exported to test
export const get_paths = ({
	refs,
	target_condition,
	get_next_refs,
}: {
	refs: number[]
	target_condition: (ref: number) => boolean
	get_next_refs: (ref: number) => number[]
}) => {
	const paths: number[][] = []

	refs.forEach(ref => target_condition(ref)
		? paths.push([ ref ])
		: get_paths({
			refs: get_next_refs(ref),
			target_condition,
			get_next_refs,
		}).forEach(path => path.length && paths.push([ ref, ...path ]))
	)

	return paths
}
